import React, { memo, useEffect } from 'react'

import { commonGridColDefs } from '@/entities/columnDefinitions/commonColDefs'
import { getGridColDefs } from '@/entities/columnDefinitions/libs/getColDef'
import { fetchUnassignedOrdersByScenario } from '@/entities/orders/thunks/fetchUnassignedOrdersByScenario'
import { scenariosSelectors } from '@/entities/schedules'
import { selectBusinessModel } from '@/entities/user'
import { openRightSidebar, setLastSelectedStopId } from '@/entities/userInterface/slices/userInterfaceSlice'
import { setDetailViewedOrder } from '@/entities/userInterface/thunks/setDetailViewedOrder'
import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { GridRowActionItem } from '@/shared/components/GridTable/models/GridRowActions'
import { TREE_WIDGETS, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faFileLines, faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { Typography } from '@mui/material'
import { GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-premium'

import { useColumnGroupingModel } from './libs/useColumnGroupingModel'
import { usePlanningUnassignedTableData } from './libs/usePlanningUnassignedTableData'
import { PlanningUnassignedTableDropZone } from './PlanningUnassignedTableDropZone'

export type PlanningUnassignedTableProps = {
  widgetId: WidgetTypeWithSuffix // Widget Window id
  id: string // Planning Scenario Id
}

export const PlanningUnassignedTable: React.FC<PlanningUnassignedTableProps> = memo(({ widgetId, id }) => {
  const { rows, isLoading } = usePlanningUnassignedTableData(id)
  const { columnGroupingModel, initialColumnGroupVisibility } = useColumnGroupingModel()
  const businessModel = useAppSelector(selectBusinessModel)
  const scenarioName = useAppSelector(state => scenariosSelectors.selectById(state, id)?.name)
  const selectedSearchResult = useAppSelector(state => state.userInterface.selectedSearchResult)
  const columns = getGridColDefs({
    gridTypeName: 'scenarioUnassigned',
    additionalColDefs: [commonGridColDefs.geocodeError],
  })
  
  const dispatch = useAppDispatch()
  const apiRef = useGridApiRef()

  // Update global store with widow title, uiColor, etc.
  useEffect(() => {
    if (scenarioName) {
      dispatch(upsertOneWidgetWindow({
        id: widgetId,
        scenarioId: id,
        isActive: true,
        widgetType: TREE_WIDGETS.ScenarioUnassignedOrders,
        navigations: [
          {
            title: scenarioName || '',
          },
          {
            title: `Unassigned (${rows?.length || 0} Orders)`,
            icon: faFileLines,
          },
        ],
      }))
    }
  }, [dispatch, widgetId, scenarioName, businessModel, rows?.length, id])

  // Update Selected rows when search result change
  useEffect(() => {
    if (selectedSearchResult && selectedSearchResult.isPlanning && selectedSearchResult.scheduleId == id &&
      selectedSearchResult.orderId && apiRef?.current?.getRow(selectedSearchResult.orderId)) {
      apiRef.current?.selectRows([selectedSearchResult.orderId], true, true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSearchResult, id])

  // Retreieve unassigned orders by scenario
  useEffect(() => {
    dispatch(fetchUnassignedOrdersByScenario({ scenarioId: id }))
  }, [dispatch, id])

  const onGridRowClick = (selectedRowData: GridValidRowModel) => {
    dispatch(setLastSelectedStopId(selectedRowData.id))
  }

  const viewOrderDetailAction: GridRowActionItem = {
    icon: faUpRightFromSquare,
    label: 'View Order',
    onClick: (rowData: GridValidRowModel) => {
      if (rowData.orderId) {
        dispatch(openRightSidebar())
        dispatch(setDetailViewedOrder({ orderId: rowData.orderId }))
      }
    },
  }

  if (!rows) {
    return <Typography variant="body1">Loading...</Typography>
  }

  return (
    <>
      <GridTable
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        checkboxSelection
        isDragDropEnabled
        loading={isLoading}
        disableVirtualization
        pinnedLeftColumns={['geocodeError']}
        onRowClick={onGridRowClick}
        hotAction={viewOrderDetailAction}
        initialColumnGroupVisibility={initialColumnGroupVisibility}
      />
      <PlanningUnassignedTableDropZone scenarioId={id}/>
    </>

  )
}, (prevProps, nextProps) => {
  return prevProps.id === nextProps.id && prevProps.widgetId === nextProps.widgetId
})
